import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import type {
  AggregateOffer,
  EventAttendanceModeEnumeration,
  GeoCoordinates,
  ItemAvailability,
  ItemList,
  MerchantReturnPolicy,
  PostalAddress,
  Thing,
  WithContext,
} from 'schema-dts'
import { KONFETTI_COMPANY } from '~~/constants/company'

dayjs.extend(utc)
dayjs.extend(timezone)

const DEFAULT_TIMEZONE = 'Europe/Berlin'
const LIMITED_AVAILABILITY_THRESHOLD = 5

// Formatted addresses end in a localized country name ("Deutschland"), while
// Google wants ISO 3166-1 alpha-2 codes. Other countries keep their name.
const countryCodeByName = new Map(
  ['de', 'en'].flatMap((language) => {
    const names = new Intl.DisplayNames([language], { type: 'region' })
    return ['DE', 'AT', 'CH', 'NL', 'BE', 'LU', 'FR', 'IT', 'ES'].map(
      (code) => [names.of(code)?.toLowerCase(), code] as const
    )
  })
)

export const schemaEventAttendanceModes: Record<
  string,
  EventAttendanceModeEnumeration
> = {
  HYBRID: 'https://schema.org/MixedEventAttendanceMode',
  IN_PERSON: 'https://schema.org/OfflineEventAttendanceMode',
  NATIONWIDE: 'https://schema.org/OfflineEventAttendanceMode',
  ONLINE: 'https://schema.org/OnlineEventAttendanceMode',
}

/** ISO 8601 with the local UTC offset (Berlin unless given), e.g. 2026-11-06T16:00:00+01:00. */
export const formatSchemaDate = (
  date: string | number | null | undefined,
  tz?: string | null
): string | undefined => {
  if (!date) return undefined
  const parsed = dayjs(date)
  if (!parsed.isValid()) return undefined
  return parsed.tz(tz || DEFAULT_TIMEZONE).format()
}

/**
 * Whether a date can still go into Event markup. The API's next date isn't
 * always moved on once it passes (KFT-4271), and Google treats past or
 * expired dates as misleading, so those get no markup at all.
 */
export const isSchemaDateUpcoming = (
  date: string | number | null | undefined,
  status?: string | null
): boolean => {
  if (!date || status === 'EXPIRED') return false
  const parsed = dayjs(date)
  return parsed.isValid() && parsed.isAfter(dayjs())
}

/**
 * Splits a formatted address ("Reichenbachstraße 22, 80469 München,
 * Deutschland") into PostalAddress fields. The API only ships the formatted
 * string, so the street is the first segment and the country the last one
 * when there are enough segments to tell them apart. Without a locality the
 * city is read from the "zipcode city" segment.
 */
export const getSchemaPostalAddress = ({
  formatted,
  zipcode,
  locality,
}: {
  formatted?: string | null
  zipcode?: string | null
  locality?: string | null
}): PostalAddress => {
  const segments = (formatted || '')
    .split(',')
    .map((segment) => segment.trim())
    .filter(Boolean)
  const country = segments.length >= 2 ? segments.at(-1) : undefined
  const city =
    locality ||
    (zipcode &&
      segments
        .find((segment) => segment.startsWith(`${zipcode} `))
        ?.slice(zipcode.length + 1)
        .trim())

  return {
    '@type': 'PostalAddress',
    ...(segments.length >= 3 && { streetAddress: segments[0] }),
    ...(city && { addressLocality: city }),
    ...(zipcode && { postalCode: zipcode }),
    ...(country &&
      !/\d/.test(country) && {
        addressCountry: countryCodeByName.get(country.toLowerCase()) ?? country,
      }),
  }
}

export const getSchemaGeo = (
  lat?: number | string | null,
  lng?: number | string | null
): GeoCoordinates | undefined => {
  const latitude = Number(lat)
  const longitude = Number(lng)
  if (!lat || !lng || !Number.isFinite(latitude) || !Number.isFinite(longitude))
    return undefined
  return { '@type': 'GeoCoordinates', latitude, longitude }
}

/**
 * The statutory 14-day withdrawal for goods from the AGB (§ 5,
 * Widerrufsbelehrung); the customer pays the return postage.
 */
export const getSchemaReturnPolicy = (
  countries: string[]
): MerchantReturnPolicy => ({
  '@type': 'MerchantReturnPolicy',
  applicableCountry: countries,
  returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
  merchantReturnDays: 14,
  returnMethod: 'https://schema.org/ReturnByMail',
  returnFees: 'https://schema.org/ReturnFeesCustomerResponsibility',
})

export const getSchemaOfferAvailability = (
  ticketsAvailable?: number | null
): ItemAvailability => {
  if (typeof ticketsAvailable !== 'number') return 'https://schema.org/InStock'
  if (ticketsAvailable <= 0) return 'https://schema.org/SoldOut'
  if (ticketsAvailable <= LIMITED_AVAILABILITY_THRESHOLD)
    return 'https://schema.org/LimitedAvailability'
  return 'https://schema.org/InStock'
}

/**
 * Price range over the listed items, with prices in major units. All numbers
 * come from the same items, so lowPrice, highPrice and offerCount agree.
 */
export const getSchemaAggregateOffer = (
  prices: { low: number; high: number; currency: string }[]
): AggregateOffer | undefined => {
  const valid = prices.filter(
    ({ low, high }) => Number.isFinite(low) && Number.isFinite(high)
  )
  if (!valid.length) return undefined

  return {
    '@type': 'AggregateOffer',
    lowPrice: Math.min(...valid.map(({ low }) => low)),
    highPrice: Math.max(...valid.map(({ high, low }) => Math.max(high, low))),
    priceCurrency: valid[0]!.currency,
    offerCount: valid.length,
  }
}

/**
 * Stable @id for a partner, shared by the partner page and its events.
 * Locale-free like the organization's @id, so the German and English pages
 * name the same business.
 */
export const getSchemaBusinessId = (profilePath: string): string | undefined =>
  profilePath
    ? `${KONFETTI_COMPANY.url}${profilePath.replace(/^\//, '')}#business`
    : undefined

/**
 * Wraps listing nodes in an ItemList for Google's host carousel (beta), which
 * shows Event and Product summary pages in the EEA. An exception to the
 * project's @graph rule: the carousel only reads ItemList, in list order.
 * https://developers.google.com/search/docs/appearance/structured-data/carousels-beta
 */
export const getSchemaItemList = (
  nodes: Thing[] | undefined | null
): WithContext<ItemList> | undefined => {
  if (!nodes?.length) return undefined

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: nodes.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item,
    })),
  }
}
